import styles from './RoadmapHeader.module.scss';
import * as React from 'react';
import {
  Flex,
  View,
  Heading,
  Provider as ProviderV3,
  defaultTheme,
  ToggleButton,
  ActionButton,
  Text
} from '@adobe/react-spectrum';

import Provider from '@react/react-spectrum/Provider';
import { ActionButtons } from './ActionButtons';
// import { CreateButton } from './CreateButton';
import { FilterSection } from './FilterSection';
import { ViewControls } from './ViewControls';
import { ThemeContext } from 'context/themeContext';
import { ThemeContextType } from 'types/theme';
import Maximize from '@spectrum-icons/workflow/Maximize';
import Visibility from '@spectrum-icons/workflow/Visibility';
import { HelpMenu } from './HelpMenu';
import { useHomeSlice } from 'app/pages/Home/slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDateRange,
  selectFullScreenMode,
  selectLegend,
  selectShowRefreshIndicator,
  selectHome
} from 'app/pages/Home/slice/selectors';
import { parseDate } from '@internationalized/date';
import { saveUserPreferences } from 'utils/helpers';
import { CalendarRange } from '../Fields/Calendar';
import CreateButton, { CreateButtonProps } from '../RsdForms/CreateButton';
import { FilterSectionOld } from './FilterOld/FilterSectionOld';
import { FilterChipsContainer } from './FilterChipsContainer/FilterChipsContainer';
import { useFilterChips } from './FilterChipsContainer/useFilterChips';
import { useFilterDataLoader } from './FilterChipsContainer/useFilterDataLoader';
import { ClearAllButton } from './ClearAllButton/ClearAllButton';
import { ShareViewButton } from './ClearAllButton/ShareViewButton';
import { SaveViewButton } from './ClearAllButton/SaveViewButton';
import { useSavedViewsSlice } from 'app/components/SavedViews';
import { Divider } from './FilterChipsContainer/Divider';
import { NewFilter } from './NewFilter';
import { useLocation } from 'react-router-dom';
import { IS_STAGE_ENVIRONMENT } from '../Shell';
import { TimelineViewToggle } from 'app/components/MonthlyTimeline';
import { selectIsAuthenticated } from 'app/pages/Layout/slice/selectors';
import { useViewState } from 'app/context/ViewStateContext';
import { isRoadmapRoute } from 'utils/viewStateDeepLink';

interface RoadmapHeaderProps {
  onMomentCreateSuccess?: (message: string) => void;
  showViewControls?: boolean; // Show/hide ViewControls component (Compact, In The Works)
  hideEventsFilter?: boolean; // Hide Events filter from NewFilter
  /** End of Life (EOL) moment type is roadmap-only (milestones grid report). */
  hideEolMomentType?: boolean;
  /** Include PCO and EOL in moment-type filter menu (milestones grid report). */
  milestonesGridMomentTypeMenu?: boolean;
  customLanesTiersOptions?: Array<{ key: string; label: string }>; // Custom lanes/tiers options to use instead of API data
  hideCompactToggle?: boolean; // Hide compact toggle in ViewControls (for milestone grid view)
  hideIconsToggle?: boolean; // Hide Icons switch in ViewControls (e.g. milestone grid report)
  showBetaToggle?: boolean; // Show Beta toggle in ViewControls (milestone grid)
  betaToggleLabel?: string; // Label for Beta toggle
  betaToggleSelected?: boolean; // Beta toggle selected state
  onBetaToggle?: (value: boolean) => void; // Beta toggle handler
  showTimelineViewToggle?: boolean; // Show Quarterly/Monthly view toggle (home roadmap only)
  /** Hide the filter/view-controls row entirely (pages with no moment data of their own, e.g. Review & Approval). */
  hideFilterBar?: boolean;
  /** Hide the date-range calendar picker (pages with no date-ranged moment data, e.g. Review & Approval). */
  hideCalendarRange?: boolean;
}

export function RoadmapHeader({
  onMomentCreateSuccess,
  showViewControls = true,
  hideEventsFilter = false,
  hideEolMomentType = false,
  milestonesGridMomentTypeMenu = false,
  customLanesTiersOptions,
  hideCompactToggle = false,
  hideIconsToggle = false,
  showBetaToggle = false,
  betaToggleLabel,
  betaToggleSelected,
  onBetaToggle,
  showTimelineViewToggle = false,
  hideFilterBar = false,
  hideCalendarRange = false
}: RoadmapHeaderProps = {}) {
  const getCurrentViewState = useViewState();
  const { theme, changeTheme } = React.useContext(
    ThemeContext
  ) as ThemeContextType;
  const { actions } = useHomeSlice();
  useSavedViewsSlice();
  const showLegend = useSelector(selectLegend);
  const dateRange = useSelector(selectDateRange);
  const isFullScreenMode = useSelector(selectFullScreenMode);
  const showRefreshIndicator = useSelector(selectShowRefreshIndicator);
  const homeState = useSelector(selectHome);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const location = useLocation();
  // RoadmapHeader is shared between Home (Roadmap/My Stuff) and
  // MilestonesGridReport. The shared Roadmap/My Stuff preference-restore
  // effects below (useFilterDataLoader, NewFilter, ViewControls) only make
  // sense on a Roadmap route — restoring them while first mounted on
  // Milestones races with an in-flight Saved View/My Stuff transition that
  // owns this same shared state.
  const isOnRoadmapRoute = isRoadmapRoute(location.pathname);

  // Load initial filter data from localStorage
  useFilterDataLoader(isOnRoadmapRoute);

  // Get filter chips data
  const filterChipGroups = useFilterChips({
    hideEolMomentType: milestonesGridMomentTypeMenu ? false : hideEolMomentType
  });
  const totalChips = filterChipGroups.reduce(
    (total, group) => total + group.chips.length,
    0
  );

  // Add handler for moment creation success
  const handleMomentCreateSuccess = React.useCallback(
    (message: string) => {
      // Refresh the release data
      dispatch(actions.loadReleaseData({}));
      // Call the parent's onMomentCreateSuccess if provided
      if (onMomentCreateSuccess) {
        onMomentCreateSuccess(message);
      }
    },
    [dispatch, onMomentCreateSuccess]
  );

  const [isPresentationMode, setIsPresentationMode] = React.useState(false);

  const onLegendToggle = () => {
    const newValue = !showLegend;
    dispatch(actions.setLegend(newValue));
  };

  const onCalendarRangeChange = React.useCallback(
    (dates: any) => {
      const newDateRange = {
        start: dates.start.toString(),
        end: dates.end.toString()
      };
      dispatch(actions.setDateRange(newDateRange));

      // Only call moments API if NOT on milestones route
      // The milestones grid component will react to date range changes automatically
      if (!location.pathname.includes('/milestones')) {
        dispatch(actions.loadReleaseData({}));
      }

      saveUserPreferences({ dateRange: newDateRange });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [location.pathname]
  );

  const calendarDefaultRange = React.useMemo(
    () => ({
      start: parseDate(dateRange?.start),
      end: parseDate(dateRange?.end)
    }),
    [dateRange?.start, dateRange?.end]
  );

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      dispatch(actions.setIsFullScreenMode(!!document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  React.useEffect(() => {
    if (!isFullScreenMode) {
      setIsPresentationMode(false);
    }
  }, [isFullScreenMode]);

  const togglePresentationMode = () => {
    if (!isPresentationMode) {
      setIsPresentationMode(true);
      document.documentElement.requestFullscreen();
      // Don't automatically show parking lot when entering presentation mode
      // Keep the current state of parking lot
    } else {
      setIsPresentationMode(false);
      document.exitFullscreen();
    }
  };

  const selecctedTheme =
    theme === 'dark' || theme === 'darkest' ? 'dark' : 'light';
  const onThemeChange = event => {
    const newTheme = event ? 'darkest' : 'lightest';
    changeTheme(newTheme);
  };

  return (
    <Provider theme={selecctedTheme}>
      <ProviderV3 theme={defaultTheme} colorScheme={selecctedTheme}>
        <View
          data-testid="roadmap-header-container"
          UNSAFE_className={`${styles['header-container']} ${
            styles[`header-container-${selecctedTheme}`]
          }`}
          isHidden={isFullScreenMode}
        >
          <Flex
            direction="column"
            gap="size-125"
            data-testid="roadmap-header-content"
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              wrap
              data-testid="roadmap-header-top-row"
            >
              <Flex
                wrap
                gap="size-200"
                alignItems="center"
                data-testid="roadmap-header-left-section"
              >
                <Heading
                  UNSAFE_className={styles['header-title']}
                  level={1}
                  data-testid="roadmap-header-title"
                >
                  C&P Major Moments
                  {IS_STAGE_ENVIRONMENT && (
                    <span className={styles.stageBadge}>Stage</span>
                  )}
                </Heading>
                {/* <ActionButtons /> */}
                <CreateButton onSuccess={handleMomentCreateSuccess} />
              </Flex>

              {/* Subtle refresh indicator - shows when data is refreshing in background */}
              {showRefreshIndicator && (
                <div
                  className={styles['subtle-refresh-indicator']}
                  data-testid="header-refresh-indicator"
                >
                  <div className={styles['refresh-pulse']} />
                  <Text UNSAFE_className={styles['refresh-text']}>
                    Refreshing...
                  </Text>
                </div>
              )}

              <Flex
                UNSAFE_className={styles['header-options']}
                data-testid="roadmap-header-right-section"
              >
                {showTimelineViewToggle && <TimelineViewToggle />}
                {!hideCalendarRange && (
                  <CalendarRange
                    data-testid="roadmap-header-calendar"
                    defaultRange={calendarDefaultRange}
                    onCalendarRangeChange={onCalendarRangeChange}
                  />
                )}
                {/* <DateRangePicker
                  maxVisibleMonths={3}
                  defaultValue={{
                    start: calculateDefaultStart().start,
                    end: calculateDefaultStart().end
                  }}
                  value={{
                    start: parseDate(dateRange.start),
                    end: parseDate(dateRange.end)
                  }}
                  onChange={onSelectDateRange}
                  UNSAFE_className={styles['header-date-picker']}
                /> */}
                <ToggleButton
                  data-testid="roadmap-header-presentation-mode-toggle"
                  aria-label="Presentation Mode"
                  onChange={togglePresentationMode}
                  isSelected={isPresentationMode}
                  UNSAFE_className={styles['presentation-toggle']}
                >
                  <Maximize />
                  <Text UNSAFE_style={{ fontWeight: '700' }}>
                    Presentation Mode
                  </Text>
                </ToggleButton>
                <ToggleButton
                  data-testid="roadmap-header-legend-toggle"
                  aria-label="Legend"
                  isSelected={showLegend}
                  onChange={onLegendToggle}
                  UNSAFE_className={styles['legend-toggle']}
                >
                  <Visibility />
                  <Text UNSAFE_style={{ fontWeight: '700' }}>Legend</Text>
                </ToggleButton>
                {/* ShareViewButton removed */}
                <HelpMenu />
              </Flex>
            </Flex>
            {!hideFilterBar && (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                data-testid="roadmap-header-bottom-row"
                gap={'60px'}
              >
                {/* <FilterSection/> */}
                {/* <FilterSectionOld /> */}
                <Flex
                  alignItems="center"
                  minWidth="0"
                  UNSAFE_style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    minHeight: '50px'
                  }}
                >
                  <NewFilter
                    hideEventsFilter={hideEventsFilter}
                    hideEolMomentType={hideEolMomentType}
                    milestonesGridMomentTypeMenu={milestonesGridMomentTypeMenu}
                    customLanesTiersOptions={customLanesTiersOptions}
                    isRoadmapRoute={isOnRoadmapRoute}
                  />
                  {totalChips > 0 && (
                    <span style={{ marginLeft: '8px', height: '24px' }}>
                      <Divider />
                    </span>
                  )}
                  <FilterChipsContainer groups={filterChipGroups} />
                  {totalChips > 0 && <ClearAllButton />}
                  {getCurrentViewState && (
                    <>
                      <span style={{ marginLeft: '12px', height: '24px' }}>
                        <Divider />
                      </span>
                      <ShareViewButton />
                      {isAuthenticated && <SaveViewButton />}
                    </>
                  )}
                </Flex>
                {showViewControls && (
                  <Flex
                    alignItems="center"
                    gap="size-200"
                    UNSAFE_style={{ flexShrink: 0 }}
                  >
                    <ViewControls
                      hideCompactToggle={hideCompactToggle}
                      hideIconsToggle={hideIconsToggle}
                      showBetaToggle={showBetaToggle}
                      betaToggleLabel={betaToggleLabel}
                      betaToggleSelected={betaToggleSelected}
                      onBetaToggle={onBetaToggle}
                      isRoadmapRoute={isOnRoadmapRoute}
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        </View>
      </ProviderV3>
    </Provider>
  );
}

export type { RoadmapHeaderProps };
